import badgeVariables from "@krystal/css/components/badge.css?inline";
import { html, nothing, unsafeCSS } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import EkoElement from "@/utilities/element";
import { createSlotChangeHandler } from "@/utilities/slot";
import "@/components/flag";
import "@/components/avatar";
import "@/components/icon";
import "@/components/dot";
import "@/components/button";
import style from "./badge.css?inline";
import slot from "./css/slot.css?inline";
import theme from "./css/theme.css?inline";
import variant from "./css/variant.css?inline";

type SlottedElement = "eko-avatar" | "eko-flag" | "eko-icon" | "eko-dot" | null;

@customElement("eko-badge")
class Badge extends EkoElement {
  static styles = [
    ...EkoElement.styles,
    unsafeCSS(badgeVariables),
    unsafeCSS(style),
    unsafeCSS(theme),
    unsafeCSS(variant),
    unsafeCSS(slot),
  ];

  private handleKeydown = (event: KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
      event.preventDefault();
      this.dismiss();
    }
  };

  private handleSlotChange = createSlotChangeHandler(this, {
    after: "afterElement",
    before: "beforeElement",
  });

  @state()
  afterElement: SlottedElement = null;

  @state()
  beforeElement: SlottedElement = null;

  @property({ type: Boolean })
  dismissible = false;

  @property({ reflect: true, type: Boolean })
  iconOnly: boolean = false;

  @property({ type: Boolean })
  isExiting = false;

  @property({ reflect: true, type: Boolean })
  mono: boolean = false;

  @property({ reflect: true })
  size: "sm" | "md" | "lg" = "sm";

  @property({ reflect: true })
  theme: string = "default";

  @property({ reflect: true, type: Boolean })
  truncate: boolean = false;

  @property({ reflect: true })
  variant: "pill" | "pill-solid" | "square" | "square-solid" | "modern" = "pill";

  // Dismiss event
  private dismiss() {
    if (this.isExiting) return;

    this.isExiting = true;
    this.setAttribute("exiting", "");

    // Wait for exiting CSS animation to complete
    this.addEventListener("animationend", () => {
      this.dispatchEvent(new CustomEvent("dismiss", { bubbles: true }));
    });
  }

  render() {
    return html`
      <div after=${this.afterElement} before=${this.beforeElement} class="eko-badge" part="badge">
        <slot name="before" @slotchange=${this.handleSlotChange}></slot>
        <span class="eko-badge__content" part="content">
          <slot @slotchange=${this.handleSlotChange}></slot>
        </span>
        <slot name="after" @slotchange=${this.handleSlotChange}></slot>
        ${this.dismissible
          ? html`<eko-icon
              class="eko-badge__close"
              name="x-close"
              size="sm"
              role="button"
              tabindex="0"
              @click=${this.dismiss}
              @keydown=${this.handleKeydown}>
            </eko-icon>`
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-badge": Badge;
  }
}

export default Badge;
